import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Loader2, Save, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface UserProfile {
  id: string;
  user_id: string;
  email: string;
  credits: number;
  created_at: string;
}

export default function Admin() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingCredits, setEditingCredits] = useState<Record<string, number>>({});
  const [savingUser, setSavingUser] = useState<string | null>(null);
  
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [isAdmin]);

  const fetchUsers = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({ title: 'Error fetching users', variant: 'destructive' });
      return;
    }

    setUsers(data || []);
    setLoading(false);
  };

  const handleCreditChange = (userId: string, value: number) => {
    setEditingCredits((prev) => ({ ...prev, [userId]: value }));
  };

  const saveCredits = async (profile: UserProfile) => {
    const newCredits = editingCredits[profile.id];
    if (newCredits === undefined || newCredits === profile.credits) return;

    setSavingUser(profile.id);
    
    const { error } = await supabase
      .from('profiles')
      .update({ credits: newCredits })
      .eq('id', profile.id);

    if (error) {
      toast({ title: 'Error updating credits', variant: 'destructive' });
    } else {
      toast({ title: 'Credits updated successfully' });
      setUsers((prev) =>
        prev.map((u) => (u.id === profile.id ? { ...u, credits: newCredits } : u))
      );
      setEditingCredits((prev) => {
        const updated = { ...prev };
        delete updated[profile.id];
        return updated;
      });
    }

    setSavingUser(null);
  };

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Studio
          </Button>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-secondary/30"
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {filteredUsers.length} users
            </span>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={editingCredits[user.id] ?? user.credits}
                        onChange={(e) =>
                          handleCreditChange(user.id, parseInt(e.target.value) || 0)
                        }
                        className="w-24 bg-secondary/30"
                        min={0}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => saveCredits(user)}
                        disabled={
                          savingUser === user.id ||
                          editingCredits[user.id] === undefined ||
                          editingCredits[user.id] === user.credits
                        }
                      >
                        {savingUser === user.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
